type JsonSafe = null | boolean | number | string | JsonSafe[] | { [key: string]: JsonSafe };

export const safeStringify = (value: unknown, maxDepth = 6): string => {
  const seenInCap = new WeakSet<object>();
  const seenInStringify = new WeakSet<object>();

  const isObject = (v: unknown): v is object => typeof v === 'object' && v !== null;

  const toErrorObject = (err: Error, depth: number, cap: (v: unknown, d: number) => JsonSafe): JsonSafe => {
    const base: Record<string, JsonSafe> = {
      name: err.name,
      message: err.message,
      stack: err.stack ?? null,
    };

    // Include a few additional enumerable props, safely
    for (const k of Object.keys(err).slice(0, 20)) {
      try {
        const val = (err as unknown as Record<string, unknown>)[k];
        base[k] = cap(val, depth - 1);
      } catch (e) {
        base[k] = `[Thrown: ${messageOf(e)}]`;
      }
    }

    return base;
  };

  const messageOf = (e: unknown): string => {
    if (e instanceof Error) return e.message;

    return String(e);
  };

  const cap = (v: unknown, depth: number): JsonSafe => {
    // BigInt -> string (JSON doesn't support bigint)
    if (typeof v === 'bigint') return v.toString();

    if (depth <= 0) {
      if (Array.isArray(v)) return `[Array(${v.length})]`;
      if (isObject(v)) return '[Object]';
      // primitives / null / undefined

      return (v ?? null) as JsonSafe; // undefined becomes null-ish placeholder below
    }

    // primitives / null / undefined
    if (!isObject(v)) {
      // JSON.stringify drops undefined; we preserve it as a string to avoid silent loss
      if (typeof v === 'undefined') return '[Undefined]';

      return v as JsonSafe;
    }

    // cycle protection during pre-walk
    if (seenInCap.has(v)) return '[Circular]';
    seenInCap.add(v);

    // Special cases
    if (v instanceof Error) return toErrorObject(v, depth, cap);
    if (v instanceof Date) return Number.isNaN(v.getTime()) ? '[Invalid Date]' : v.toISOString();
    if (v instanceof RegExp) return v.toString();

    if (v instanceof Map) {
      const entries = Array.from(v.entries()).slice(0, 50);

      return entries.map(([k, val]) => [cap(k, depth - 1), cap(val, depth - 1)]);
    }

    if (v instanceof Set) {
      const vals = Array.from(v.values()).slice(0, 50);

      return vals.map((x) => cap(x, depth - 1));
    }

    if (ArrayBuffer.isView(v) && !(v instanceof DataView)) {
      // Typed arrays: keep it compact
      return `[${v.constructor.name}(${v.byteLength})]`;
    }

    if (Array.isArray(v)) {
      return v.slice(0, 50).map((x) => cap(x, depth - 1));
    }

    // Plain object-ish: copy enumerable own properties
    const out: Record<string, JsonSafe> = {};
    const rec = v as Record<string, unknown>;

    for (const k of Object.keys(rec).slice(0, 50)) {
      try {
        out[k] = cap(rec[k], depth - 1);
      } catch (e) {
        out[k] = `[Thrown: ${messageOf(e)}]`;
      }
    }

    return out;
  };

  const replacer: (this: unknown, key: string, val: unknown) => unknown = (_key, val) => {
    // BigInt might still appear (belt + suspenders)
    if (typeof val === 'bigint') return val.toString();

    // Circular protection in stringify phase (mostly redundant once cap() breaks cycles,
    // but still helps if something sneaks through)
    if (isObject(val)) {
      if (seenInStringify.has(val)) return '[Circular]';
      seenInStringify.add(val);
    }

    return val;
  };

  try {
    return JSON.stringify(cap(value, maxDepth), replacer);
  } catch (e) {
    return `[Unstringifiable payload: ${messageOf(e)}]`;
  }
};
